import * as THREE from 'three';
import { IScene } from './scenes/base-scene';

/**
 * Manages multiple scenes and their lifecycle
 */
export class SceneManager {
  private scenes: Map<string, IScene> = new Map();
  private activeScenes: Set<string> = new Set();
  private clock: THREE.Clock = new THREE.Clock();

  /**
   * Register a scene with the manager
   */
  registerScene(scene: IScene): void {
    if (this.scenes.has(scene.metadata.id)) {
      console.warn(`Scene with id '${scene.metadata.id}' is already registered`);
      return;
    }
    this.scenes.set(scene.metadata.id, scene);
    console.log(`✅ Scene registered: ${scene.metadata.name} (${scene.metadata.id})`);
  }

  /**
   * Get a scene by ID
   */
  getScene(id: string): IScene | undefined {
    return this.scenes.get(id);
  }

  /**
   * Check if a scene is registered
   */
  hasScene(id: string): boolean {
    return this.scenes.has(id);
  }

  /**
   * List all registered scenes
   */
  listScenes(): Array<{ id: string; name: string; description: string; active: boolean }> {
    return Array.from(this.scenes.values()).map(scene => ({
      id: scene.metadata.id,
      name: scene.metadata.name,
      description: scene.metadata.description,
      active: this.activeScenes.has(scene.metadata.id)
    }));
  }

  /**
   * Initialize a scene (mounts it to a parent)
   */
  initScene(id: string, parent: THREE.Object3D, renderer: THREE.WebGLRenderer): boolean {
    const scene = this.scenes.get(id);
    if (!scene) {
      console.error(`Scene '${id}' not found`);
      return false;
    }

    scene.init(parent, renderer);
    this.activeScenes.add(id);
    console.log(`🎬 Scene initialized: ${scene.metadata.name}`);
    return true;
  }

  /**
   * Update all active scenes
   */
  updateActiveScenes(): void {
    const delta = this.clock.getDelta();
    for (const sceneId of this.activeScenes) {
      const scene = this.scenes.get(sceneId);
      if (scene) {
        scene.update(delta);
      }
    }
  }

  /**
   * Dispose of a scene
   */
  disposeScene(id: string): void {
    const scene = this.scenes.get(id);
    if (!scene) {
      console.error(`Scene '${id}' not found`);
      return;
    }

    scene.dispose();
    this.activeScenes.delete(id);
    console.log(`🗑️ Scene disposed: ${scene.metadata.name}`);
  }

  /**
   * Set visibility of a scene
   */
  setSceneVisible(id: string, visible: boolean): void {
    const scene = this.scenes.get(id);
    if (scene) {
      scene.setVisible(visible);
    }
  }

  /**
   * Set the transform (position/rotation/scale) of a scene
   */
  setSceneTransform(
    id: string,
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3
  ): void {
    const scene = this.scenes.get(id);
    if (scene) {
      scene.setOrigin(position, rotation, scale);
      console.log(`📍 Scene '${id}' transform updated`);
    } else {
      console.error(`Scene '${id}' not found`);
    }
  }

  /**
   * Get a scene's current position
   */
  getScenePosition(id: string): THREE.Vector3 | null {
    const scene = this.scenes.get(id);
    return scene ? scene.getPosition() : null;
  }

  /**
   * Get a scene's world center
   */
  getSceneCenter(id: string): THREE.Vector3 | null {
    const scene = this.scenes.get(id);
    return scene ? scene.getWorldCenter() : null;
  }

  /**
   * Dispose of all scenes
   */
  disposeAll(): void {
    for (const scene of this.scenes.values()) {
      scene.dispose();
    }
    this.scenes.clear();
    this.activeScenes.clear();
  }
}
